"use client";

import { useEffect, useId, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
	ChevronDownIcon,
	MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "@/hooks/useTranslation";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
	buildInternationalPhoneNumber,
	DEFAULT_PHONE_COUNTRY,
	getFlagEmoji,
	getLocalizedCountryName,
	inferPhoneCountry,
	isValidNationalPhoneNumber,
	normalizeNationalPhoneNumber,
	PHONE_COUNTRIES,
	type PhoneCountry,
} from "@/lib/phoneCountries";
import GlassCard from "./GlassCard";
import GradientButton from "./GradientButton";
import PhoneConfirmationModal from "./PhoneConfirmationModal";
import TermsAndConditions from "./TermsAndConditions";

interface FormData {
	name: string;
	subject: string;
	level: string;
	phone: string;
	message: string;
}

interface FormErrors {
	name?: string;
	subject?: string;
	level?: string;
	phoneCountry?: string;
	phone?: string;
	message?: string;
	terms?: string;
}

interface ContactFormProps {
	preselectedSubject?: string;
}

interface LocalizedPhoneCountry extends PhoneCountry {
	flag: string;
	localizedName: string;
}

function getPhoneCountryMatchRank(
	country: LocalizedPhoneCountry,
	searchTerm: string,
) {
	if (!searchTerm) {
		return 3;
	}

	const normalizedSearchTerm = searchTerm.toLowerCase();
	const normalizedDialCodeSearch = normalizedSearchTerm.replace(/[^\d]/g, "");
	const localizedName = country.localizedName.toLowerCase();
	const canonicalName = country.name.toLowerCase();
	const iso2 = country.iso2.toLowerCase();
	const dialCode = country.dialCode.toLowerCase();
	const normalizedDialCode = country.dialCode.replace(/[^\d]/g, "");

	if (
		localizedName === normalizedSearchTerm ||
		canonicalName === normalizedSearchTerm ||
		iso2 === normalizedSearchTerm ||
		(normalizedDialCodeSearch !== "" &&
			normalizedDialCode === normalizedDialCodeSearch)
	) {
		return 0;
	}

	if (
		localizedName.startsWith(normalizedSearchTerm) ||
		canonicalName.startsWith(normalizedSearchTerm) ||
		iso2.startsWith(normalizedSearchTerm) ||
		(normalizedDialCodeSearch !== "" &&
			normalizedDialCode.startsWith(normalizedDialCodeSearch)) ||
		dialCode.startsWith(normalizedSearchTerm)
	) {
		return 1;
	}

	return 2;
}

export default function ContactForm({ preselectedSubject }: ContactFormProps) {
	const { t, language } = useTranslation();
	const isMobile = useIsMobile();
	const [isHydrated, setIsHydrated] = useState(false);
	const [formData, setFormData] = useState<FormData>({
		name: "",
		subject: "",
		level: "",
		phone: "",
		message: "",
	});
	const [formErrors, setFormErrors] = useState<FormErrors>({});
	const [selectedPhoneCountry, setSelectedPhoneCountry] = useState<PhoneCountry>(
		DEFAULT_PHONE_COUNTRY,
	);
	const [phoneCountrySearch, setPhoneCountrySearch] = useState("");
	const [isPhoneCountryPickerOpen, setIsPhoneCountryPickerOpen] =
		useState(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<
		"idle" | "success" | "error"
	>("idle");
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [showTermsModal, setShowTermsModal] = useState(false);
	const nameId = useId();
	const subjectId = useId();
	const levelId = useId();
	const phoneId = useId();
	const messageId = useId();
	const termsId = useId();
	const phoneCountryPickerRef = useRef<HTMLDivElement>(null);
	const phoneCountrySearchRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setIsHydrated(true);

		if (typeof window === "undefined") {
			return;
		}

		const detectedPhoneCountry = inferPhoneCountry(
			window.navigator.languages,
			Intl.DateTimeFormat().resolvedOptions().timeZone,
		);

		setSelectedPhoneCountry(detectedPhoneCountry);
	}, []);

	useEffect(() => {
		if (preselectedSubject) {
			setFormData((previousFormData) => ({
				...previousFormData,
				subject: preselectedSubject,
			}));
			setFormErrors((previousErrors) => ({
				...previousErrors,
				subject: undefined,
			}));
			setSubmitStatus("idle");
		}
	}, [preselectedSubject]);

	useEffect(() => {
		const handlePreselection = (event: CustomEvent) => {
			const { classType } = event.detail;

			setFormData((previousFormData) => ({
				...previousFormData,
				subject: classType,
			}));
			setFormErrors((previousErrors) => ({
				...previousErrors,
				subject: undefined,
			}));
			setSubmitStatus("idle");
		};

		window.addEventListener(
			"preselectClassType",
			handlePreselection as EventListener,
		);

		return () => {
			window.removeEventListener(
				"preselectClassType",
				handlePreselection as EventListener,
			);
		};
	}, []);

	useEffect(() => {
		if (!isPhoneCountryPickerOpen) {
			return;
		}

		const animationFrame = window.requestAnimationFrame(() => {
			phoneCountrySearchRef.current?.focus();
		});

		return () => {
			window.cancelAnimationFrame(animationFrame);
		};
	}, [isPhoneCountryPickerOpen]);

	useEffect(() => {
		if (!isPhoneCountryPickerOpen) {
			return;
		}

		const handlePointerDown = (event: MouseEvent) => {
			if (
				phoneCountryPickerRef.current &&
				!phoneCountryPickerRef.current.contains(event.target as Node)
			) {
				setIsPhoneCountryPickerOpen(false);
			}
		};

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setIsPhoneCountryPickerOpen(false);
			}
		};

		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleEscape);

		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isPhoneCountryPickerOpen]);

	const clearError = (field: keyof FormErrors) => {
		setFormErrors((previousErrors) => {
			if (!previousErrors[field]) {
				return previousErrors;
			}

			return {
				...previousErrors,
				[field]: undefined,
			};
		});
	};

	const resetSubmitStatus = () => {
		if (submitStatus !== "idle") {
			setSubmitStatus("idle");
		}
	};

	const phoneCountries: LocalizedPhoneCountry[] = PHONE_COUNTRIES.map((country) => ({
		...country,
		flag: getFlagEmoji(country.iso2),
		localizedName: getLocalizedCountryName(country, language),
	}));

	const normalizedPhoneCountrySearch = phoneCountrySearch.trim().toLowerCase();

	const filteredPhoneCountries = phoneCountries
		.filter((country) => {
			if (!normalizedPhoneCountrySearch) {
				return true;
			}

			const normalizedDialCodeSearch =
				normalizedPhoneCountrySearch.replace(/[^\d]/g, "");
			const localizedName = country.localizedName.toLowerCase();
			const canonicalName = country.name.toLowerCase();
			const normalizedDialCode = country.dialCode.replace(/[^\d]/g, "");

			return (
				localizedName.includes(normalizedPhoneCountrySearch) ||
				canonicalName.includes(normalizedPhoneCountrySearch) ||
				country.iso2.toLowerCase().includes(normalizedPhoneCountrySearch) ||
				country.dialCode.includes(normalizedPhoneCountrySearch) ||
				(normalizedDialCodeSearch !== "" &&
					normalizedDialCode.startsWith(normalizedDialCodeSearch))
			);
		})
		.sort((firstCountry, secondCountry) => {
			const firstCountryRank = getPhoneCountryMatchRank(
				firstCountry,
				normalizedPhoneCountrySearch,
			);
			const secondCountryRank = getPhoneCountryMatchRank(
				secondCountry,
				normalizedPhoneCountrySearch,
			);

			if (firstCountryRank !== secondCountryRank) {
				return firstCountryRank - secondCountryRank;
			}

			return firstCountry.localizedName.localeCompare(
				secondCountry.localizedName,
				language,
			);
		});

	const normalizedPhoneNumber = normalizeNationalPhoneNumber(formData.phone);
	const selectedPhoneCountryName = getLocalizedCountryName(
		selectedPhoneCountry,
		language,
	);
	const shouldAnimate = isHydrated && !isMobile;

	const handleFormValueChange = <K extends keyof FormData>(
		field: K,
		value: FormData[K],
	) => {
		setFormData((previousFormData) => ({
			...previousFormData,
			[field]: value,
		}));
		clearError(field as keyof FormErrors);
		resetSubmitStatus();
	};

	const handlePhoneCountrySelect = (country: PhoneCountry) => {
		setSelectedPhoneCountry(country);
		setPhoneCountrySearch("");
		setIsPhoneCountryPickerOpen(false);
		clearError("phoneCountry");
		resetSubmitStatus();
	};

	const validateForm = () => {
		const errors: FormErrors = {};
		const normalizedPhone = normalizeNationalPhoneNumber(formData.phone);

		if (!formData.name.trim()) {
			errors.name = t("contact.form.validation.nameRequired");
		}

		if (!formData.subject.trim()) {
			errors.subject = t("contact.form.validation.subjectRequired");
		}

		if (!formData.level.trim()) {
			errors.level = t("contact.form.validation.levelRequired");
		}

		if (!selectedPhoneCountry?.dialCode) {
			errors.phoneCountry = t("contact.form.validation.phoneCountryRequired");
		}

		if (!normalizedPhone) {
			errors.phone = t("contact.form.validation.phoneRequired");
		} else if (!isValidNationalPhoneNumber(normalizedPhone)) {
			errors.phone = t("contact.form.validation.phoneInvalid");
		}

		if (!formData.message.trim()) {
			errors.message = t("contact.form.validation.messageRequired");
		}

		if (!termsAccepted) {
			errors.terms = t("terms.required");
		}

		setFormErrors(errors);

		if (Object.keys(errors).length > 0) {
			return null;
		}

		return normalizedPhone;
	};

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		const validatedPhoneNumber = validateForm();

		if (!validatedPhoneNumber) {
			return;
		}

		if (validatedPhoneNumber !== formData.phone) {
			setFormData((previousFormData) => ({
				...previousFormData,
				phone: validatedPhoneNumber,
			}));
		}

		setIsPhoneCountryPickerOpen(false);
		setIsConfirmModalOpen(true);
	};

	const confirmSubmit = async () => {
		const validatedPhoneNumber = validateForm();

		if (!validatedPhoneNumber) {
			setIsConfirmModalOpen(false);
			return;
		}

		const localizedCountryName = getLocalizedCountryName(
			selectedPhoneCountry,
			language,
		);
		const phone = buildInternationalPhoneNumber(
			selectedPhoneCountry.dialCode,
			validatedPhoneNumber,
		);

		setIsSubmitting(true);
		setSubmitStatus("idle");

		try {
			const response = await fetch("/api/contact", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					...formData,
					phone,
					phoneCountryIso: selectedPhoneCountry.iso2,
					phoneCountryName: localizedCountryName,
					phoneDialCode: selectedPhoneCountry.dialCode,
				}),
			});

			if (!response.ok) {
				setSubmitStatus("error");
				setIsConfirmModalOpen(false);
				return;
			}

			setSubmitStatus("success");
			setFormData({
				name: "",
				subject: "",
				level: "",
				phone: "",
				message: "",
			});
			setTermsAccepted(false);
			setFormErrors({});
			setIsConfirmModalOpen(false);
			setPhoneCountrySearch("");
		} catch (error) {
			console.error("Error submitting form:", error);
			setSubmitStatus("error");
			setIsConfirmModalOpen(false);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleTermsAccept = () => {
		setTermsAccepted(true);
		clearError("terms");
	};

	return (
		<section className="py-10 px-4">
			<div className="mx-auto max-w-4xl">
				<motion.div
					initial={shouldAnimate ? { opacity: 0, y: 50 } : undefined}
					whileInView={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
					transition={shouldAnimate ? { duration: 0.8 } : undefined}
					viewport={shouldAnimate ? { once: true } : undefined}
					className="mb-12 text-center"
				>
					<h2 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
						{t("contact.title")}
					</h2>
					<p className="text-xl text-gray-700">{t("contact.subtitle")}</p>
				</motion.div>

				<motion.div
					initial={shouldAnimate ? { opacity: 0, y: 30 } : undefined}
					whileInView={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
					transition={
						shouldAnimate ? { duration: 0.8, delay: 0.2 } : undefined
					}
					viewport={shouldAnimate ? { once: true } : undefined}
				>
					<GlassCard className="p-8">
						<form onSubmit={handleSubmit} className="space-y-6">
							<div>
								<label
									htmlFor={nameId}
									className="mb-2 block text-sm font-medium text-gray-700"
								>
									{t("contact.form.name")} *
								</label>
								<input
									id={nameId}
									type="text"
									value={formData.name}
									onChange={(event) =>
										handleFormValueChange("name", event.target.value)
									}
									autoComplete="name"
									placeholder={t("contact.form.namePlaceholder")}
									aria-invalid={!!formErrors.name}
									aria-describedby={formErrors.name ? `${nameId}-error` : undefined}
									className={`w-full rounded-lg border bg-white px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 ${formErrors.name ? "border-red-500" : "border-gray-300"
										}`}
								/>
								{formErrors.name && (
									<p id={`${nameId}-error`} className="mt-1 text-sm text-red-500" role="alert">{formErrors.name}</p>
								)}
							</div>

							<div>
								<label
									htmlFor={subjectId}
									className="mb-2 block text-sm font-medium text-gray-700"
								>
									{t("contact.form.subject")} *
								</label>
								<select
									id={subjectId}
									value={formData.subject}
									onChange={(event) =>
										handleFormValueChange("subject", event.target.value)
									}
									aria-invalid={!!formErrors.subject}
									aria-describedby={formErrors.subject ? `${subjectId}-error` : undefined}
									className={`w-full rounded-lg border bg-white px-4 py-3 text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 ${formErrors.subject ? "border-red-500" : "border-gray-300"
										}`}
								>
									<option value="" disabled>
										{t("contact.form.selectSubject")}
									</option>
									<option value="trial">{t("contact.classTypes.trial")}</option>
									<option value="mini">{t("pricing.mini.name")}</option>
									<option value="standard">{t("pricing.standard.name")}</option>
									<option value="intensive">{t("pricing.intensive.name")}</option>
									<option value="other">{t("contact.form.other")}</option>
								</select>
								{formErrors.subject && (
									<p id={`${subjectId}-error`} className="mt-1 text-sm text-red-500" role="alert">
										{formErrors.subject}
									</p>
								)}
							</div>

							<div>
								<label
									htmlFor={levelId}
									className="mb-2 block text-sm font-medium text-gray-700"
								>
									{t("contact.form.level")} *
								</label>
								<select
									id={levelId}
									value={formData.level}
									onChange={(event) =>
										handleFormValueChange("level", event.target.value)
									}
									aria-invalid={!!formErrors.level}
									aria-describedby={formErrors.level ? `${levelId}-error` : undefined}
									className={`w-full rounded-lg border bg-white px-4 py-3 text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 ${formErrors.level ? "border-red-500" : "border-gray-300"
										}`}
								>
									<option value="" disabled>
										{t("contact.form.selectLevel")}
									</option>
									<option value="null">{t("contact.form.levels.null")}</option>
									<option value="A1">{t("contact.form.levels.A1")}</option>
									<option value="A2">{t("contact.form.levels.A2")}</option>
									<option value="B1">{t("contact.form.levels.B1")}</option>
									<option value="B2">{t("contact.form.levels.B2")}</option>
									<option value="C1">{t("contact.form.levels.C1")}</option>
									<option value="C2">{t("contact.form.levels.C2")}</option>
								</select>
								{formErrors.level && (
									<p id={`${levelId}-error`} className="mt-1 text-sm text-red-500" role="alert">
										{formErrors.level}
									</p>
								)}
							</div>

							<div>
								<label
									htmlFor={phoneId}
									className="mb-2 block text-sm font-medium text-gray-700"
								>
									{t("contact.form.phone.label")} *
								</label>
								<div className="grid grid-cols-[108px_1fr] gap-3 sm:grid-cols-[minmax(220px,260px)_1fr]">
									<div className="relative" ref={phoneCountryPickerRef}>
										<button
											type="button"
											onClick={() =>
												setIsPhoneCountryPickerOpen((isOpen) => !isOpen)
											}
											aria-expanded={isPhoneCountryPickerOpen}
											aria-haspopup="listbox"
											aria-controls={isPhoneCountryPickerOpen ? `${phoneId}-country-list` : undefined}
											className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2.5 text-left text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 sm:px-4 ${formErrors.phoneCountry
												? "border-red-500"
												: "border-gray-300"
												}`}
										>
											<span className="min-w-0 flex-1">
												<span className="flex items-center gap-2 sm:gap-3">
													<span className="text-lg">
														{getFlagEmoji(selectedPhoneCountry.iso2)}
													</span>
													<span className="min-w-0 flex items-center gap-1.5 sm:gap-2">
														<span className="hidden truncate text-sm font-medium sm:inline">
															{selectedPhoneCountryName}
														</span>
														<span className="shrink-0 pr-1 text-sm text-gray-500">
															{selectedPhoneCountry.dialCode}
														</span>
													</span>
												</span>
											</span>
											<ChevronDownIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
										</button>

										{isPhoneCountryPickerOpen && (
											<div id={`${phoneId}-country-list`} className="absolute left-0 top-[calc(100%+0.5rem)] z-20 w-[260px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl sm:right-0 sm:w-auto" role="listbox" aria-label={t("contact.form.phone.countryLabel")}>
												<div className="border-b border-gray-200 p-3">
													<label htmlFor={`${phoneId}-country-search`} className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
														{t("contact.form.phone.countryLabel")}
													</label>
													<div className="relative">
														<MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
														<input
															id={`${phoneId}-country-search`}
															ref={phoneCountrySearchRef}
															type="text"
															value={phoneCountrySearch}
															onChange={(event) =>
																setPhoneCountrySearch(event.target.value)
															}
															onKeyDown={(event) => {
																if (event.key === "Enter") {
																	event.preventDefault();
																}
															}}
															placeholder={t(
																"contact.form.phone.searchPlaceholder",
															)}
															className="w-full rounded-xl border border-gray-200 py-2 pl-10 pr-3 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400"
														/>
													</div>
												</div>

												<div className="max-h-72 overflow-y-auto py-2">
													{filteredPhoneCountries.length > 0 ? (
														filteredPhoneCountries.map((country) => {
															const isSelected =
																country.iso2 === selectedPhoneCountry.iso2;

															return (
																<button
																	key={country.iso2}
																	type="button"
																	onClick={() =>
																		handlePhoneCountrySelect(country)
																	}
																	role="option"
																	aria-selected={isSelected}
																	className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${isSelected
																		? "bg-blue-50 text-blue-900"
																		: "text-gray-900 hover:bg-gray-50"
																		}`}
																>
																	<span className="flex min-w-0 items-center gap-3">
																		<span className="text-lg" aria-hidden="true">
																			{country.flag}
																		</span>
																		<span className="min-w-0">
																			<span className="block truncate text-sm font-medium">
																				{country.localizedName}
																			</span>
																			<span className="block text-xs uppercase tracking-[0.12em] text-gray-500">
																				{country.iso2}
																			</span>
																		</span>
																	</span>
																	<span className="text-sm font-semibold text-gray-500">
																		{country.dialCode}
																	</span>
																</button>
															);
														})
													) : (
														<p className="px-4 py-6 text-center text-sm text-gray-500">
															{t("contact.form.phone.noResults")}
														</p>
													)}
												</div>
											</div>
										)}
									</div>

									<input
										type="tel"
										id={phoneId}
										value={formData.phone}
										onChange={(event) =>
											handleFormValueChange("phone", event.target.value)
										}
										inputMode="tel"
										autoComplete="tel-national"
										placeholder={t("contact.form.phone.placeholder")}
										aria-invalid={!!formErrors.phone}
										aria-describedby={formErrors.phone ? `${phoneId}-error` : undefined}
										className={`w-full rounded-lg border bg-white px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 ${formErrors.phone ? "border-red-500" : "border-gray-300"
											}`}
									/>
								</div>
								{formErrors.phoneCountry && (
									<p className="mt-1 text-sm text-red-500" role="alert">
										{formErrors.phoneCountry}
									</p>
								)}
								{formErrors.phone && (
									<p id={`${phoneId}-error`} className="mt-1 text-sm text-red-500" role="alert">{formErrors.phone}</p>
								)}
							</div>

							<div>
								<label
									htmlFor={messageId}
									className="mb-2 block text-sm font-medium text-gray-700"
								>
									{t("contact.form.message")} *
								</label>
								<textarea
									id={messageId}
									rows={5}
									value={formData.message}
									onChange={(event) =>
										handleFormValueChange("message", event.target.value)
									}
									placeholder={t("contact.form.messagePlaceholder")}
									aria-invalid={!!formErrors.message}
									aria-describedby={formErrors.message ? `${messageId}-error` : undefined}
									className={`w-full resize-none rounded-lg border bg-white px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-400 ${formErrors.message ? "border-red-500" : "border-gray-300"
										}`}
								/>
								{formErrors.message && (
									<p id={`${messageId}-error`} className="mt-1 text-sm text-red-500" role="alert">
										{formErrors.message}
									</p>
								)}
							</div>

							{submitStatus === "success" && (
								<div className="rounded-lg border border-green-500/30 bg-green-500/20 p-4 text-center text-green-300" role="status" aria-live="polite">
									{t("contact.form.success")}
								</div>
							)}

							{submitStatus === "error" && (
								<div className="rounded-lg border border-red-500/30 bg-red-500/20 p-4 text-center text-red-300" role="alert" aria-live="assertive">
									{t("contact.form.error")}
								</div>
							)}

							<div className="space-y-2">
								<div className="flex items-end space-x-3">
									<input
										type="checkbox"
										id={termsId}
										checked={termsAccepted}
										onChange={(event) => {
											setTermsAccepted(event.target.checked);
											clearError("terms");
											resetSubmitStatus();
										}}
										className={`mt-1 h-5 w-5 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 ${formErrors.terms ? "border-red-500" : ""
											}`}
									/>
									<label htmlFor={termsId} className="text-sm text-gray-700">
										{t("terms.accept")}{" "}
										<button
											type="button"
											onClick={() => setShowTermsModal(true)}
											className="text-blue-600 underline hover:text-blue-800"
										>
											{t("terms.title")}
										</button>
									</label>
								</div>
								{formErrors.terms && (
									<p className="text-sm text-red-500" role="alert">{formErrors.terms}</p>
								)}
							</div>

							<div className="text-center">
								<GradientButton
									type="submit"
									size="lg"
									disabled={isSubmitting}
									className="min-w-[200px]"
								>
									{isSubmitting ? t("contact.form.sending") : t("contact.form.send")}
								</GradientButton>
							</div>
						</form>
					</GlassCard>
				</motion.div>
			</div>

			<TermsAndConditions
				isOpen={showTermsModal}
				onClose={() => setShowTermsModal(false)}
				onAccept={handleTermsAccept}
			/>

			<PhoneConfirmationModal
				isOpen={isConfirmModalOpen}
				name={formData.name.trim()}
				countryFlag={getFlagEmoji(selectedPhoneCountry.iso2)}
				countryName={selectedPhoneCountryName}
				dialCode={selectedPhoneCountry.dialCode}
				phoneNumber={normalizedPhoneNumber}
				isSubmitting={isSubmitting}
				onClose={() => setIsConfirmModalOpen(false)}
				onConfirm={confirmSubmit}
			/>
		</section>
	);
}
